import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { AurixMark } from "@/components/site/Logo";
import {
  StudioIcon,
  MediaIcon,
  GrowthIcon,
  FmcgIcon,
} from "@/components/site/DivisionIcons";
import DivisionModal from "@/components/site/DivisionModal";
import useTilt from "@/hooks/useTilt";
import { DIVISIONS } from "@/constants/testIds";

const CARDS = [
  {
    key: "studio",
    slug: "aurix-studio",
    name: "AURIX STUDIO",
    tag: "Technology & Product",
    hook: "Build. Launch. Scale.",
    blurb:
      "Websites and apps designed to make businesses impossible to ignore.",
    long: "AURIX STUDIO is the flagship technology arm of HR ENTERPRISES. We design and ship premium websites, mobile & web apps, product interfaces and AI automations — with a delivery window of 48 hours after design approval.",
    bullets: [
      "Websites & landing pages",
      "iOS / Android / web apps",
      "Product & dashboard UI",
      "AI-powered automations",
      "Managed hosting & CI/CD",
      "Ongoing maintenance",
    ],
    cta: "Explore Studio",
    icon: StudioIcon,
    span: "lg:col-span-7",
    accent: true,
    isFlagship: true,
  },
  {
    key: "media",
    slug: "aurix-media",
    name: "AURIX MEDIA",
    tag: "Content & Creative",
    hook: "Create. Capture. Convert.",
    blurb:
      "Content that earns attention. Creatives that build brands.",
    long: "AURIX MEDIA is our creative division. From identity systems and cinematic ad films to social content and photography — we build the stories that make your brand stick in the mind of your customer.",
    bullets: [
      "Brand identity systems",
      "Ad films & commercials",
      "Social content packages",
      "Product photography",
      "Motion & post-production",
      "Editorial & copywriting",
    ],
    cta: "Explore Media",
    icon: MediaIcon,
    span: "lg:col-span-5",
  },
  {
    key: "growth",
    slug: "aurix-growth",
    name: "AURIX GROWTH",
    tag: "Marketing & Sales",
    hook: "More Leads. More Sales.",
    blurb:
      "Marketing strategies built to grow your business — not your ad spend.",
    long: "AURIX GROWTH is where strategy meets revenue. We plug into your business as an extended growth team — running performance media, SEO, outbound sales and consulting engagements that compound quarter over quarter.",
    bullets: [
      "Performance marketing",
      "SEO & content strategy",
      "B2B outbound sales",
      "CRM & lifecycle marketing",
      "Business consulting",
      "Analytics & attribution",
    ],
    cta: "Explore Growth",
    icon: GrowthIcon,
    span: "lg:col-span-5",
  },
  {
    key: "fmcg",
    slug: "aurix-fmcg-labs",
    name: "AURIX FMCG LABS",
    tag: "Consumer Products",
    hook: "Ideas Become Products.",
    blurb:
      "Building the next generation of consumer brands from concept to shelf.",
    long: "AURIX FMCG LABS is our consumer-products division. We take original ideas from R&D to retail — starting with our flagship in-house brand AB ORGANIC JUICES, and expanding into new categories over time.",
    bullets: [
      "R&D and formulation",
      "Packaging & label design",
      "Manufacturing partners",
      "Distribution planning",
      "Retail go-to-market",
      "In-house brand: AB Organic Juices",
    ],
    cta: "Explore Labs",
    icon: FmcgIcon,
    span: "lg:col-span-7",
  },
];

const DivisionCard = ({ card, idx, onPreview, onExplore }) => {
  const { ref, onMouseMove, onMouseLeave } = useTilt({ max: 6, scale: 1.015 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.7,
        delay: idx * 0.1,
        ease: [0.2, 0.8, 0.2, 1],
      }}
      className={`col-span-12 ${card.span}`}
      style={{ perspective: 1200 }}
    >
      <div
        ref={ref}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        data-testid={`${DIVISIONS.cardPrefix}-${card.key}`}
        className="card-lux group relative flex min-h-[280px] flex-col justify-between p-7 lg:min-h-[340px] lg:p-9"
        style={{
          transformStyle: "preserve-3d",
          transition: "transform 260ms ease",
        }}
      >
        {/* Mouse-following gold spotlight */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{
            background:
              "radial-gradient(600px circle at var(--tilt-mx,50%) var(--tilt-my,50%), rgba(212,175,55,0.10), transparent 40%)",
          }}
        />

        <div className="relative flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`grid h-11 w-11 place-items-center rounded-lg border ${
                card.accent
                  ? "border-[var(--gold)]/40 bg-[var(--gold)]/10 text-[var(--gold-bright)]"
                  : "border-white/10 bg-white/[0.03] text-[var(--gold-bright)]/85"
              }`}
            >
              <card.icon className="h-6 w-6" />
            </div>
            {card.isFlagship && (
              <span className="font-mono-label rounded-full border border-[var(--gold)]/30 bg-[var(--gold)]/[0.06] px-2.5 py-1 text-[9px] text-[var(--gold-bright)]">
                Flagship
              </span>
            )}
          </div>
          <span className="font-mono-label text-zinc-500">{card.tag}</span>
        </div>

        <div className="relative mt-10">
          <h3 className="font-display text-3xl font-semibold leading-tight tracking-tight text-white lg:text-4xl">
            {card.name}
          </h3>
          <p className="mt-2 font-serif-display text-[17px] italic text-zinc-200">
            {card.hook}
          </p>
          <p className="mt-3 max-w-md text-[14.5px] leading-relaxed text-zinc-400">
            {card.blurb}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onExplore(card.slug);
                }}
                data-testid={`${DIVISIONS.cardPrefix}-${card.key}-explore`}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/30 bg-[var(--gold)]/10 px-4 py-2 text-[12.5px] font-medium text-[var(--gold-bright)] transition-all duration-300 hover:border-[var(--gold)]/60 hover:bg-[var(--gold)]/15"
              >
                {card.cta}
                <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.2} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview(card);
                }}
                data-testid={`${DIVISIONS.cardPrefix}-${card.key}-preview`}
                className="text-[12.5px] font-medium text-zinc-400 underline-offset-4 transition-colors duration-300 hover:text-white hover:underline"
              >
                Quick preview
              </button>
            </div>
            {card.isFlagship && (
              <div className="flex items-center gap-2 opacity-70 transition-opacity duration-300 group-hover:opacity-100">
                <AurixMark
                  size={30}
                  className="rounded-full ring-1 ring-[var(--gold)]/30"
                />
                <span className="font-mono-label text-[9px] text-zinc-500">
                  Horse Mark
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Divisions = () => {
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  return (
    <section
      id="divisions"
      data-testid={DIVISIONS.root}
      className="relative py-24 lg:py-36"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-14 flex flex-col items-start justify-between gap-6 lg:mb-20 lg:flex-row lg:items-end">
          <div>
            <div className="mb-4 flex items-center gap-3">
              <div className="h-px w-8 bg-[var(--gold)]/60" />
              <span className="font-mono-label text-[var(--gold)]">
                01 · Business Divisions
              </span>
            </div>
            <h2 className="font-display text-4xl leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Four teams.<br />
              <span className="silver-gradient-text">One mission.</span>
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-relaxed text-zinc-400">
            Everything your business needs. One ecosystem. Zero compromises.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          {CARDS.map((card, idx) => (
            <DivisionCard
              key={card.key}
              card={card}
              idx={idx}
              onPreview={setActive}
              onExplore={(slug) => navigate(`/${slug}`)}
            />
          ))}
        </div>
      </div>

      <DivisionModal
        open={!!active}
        onClose={() => setActive(null)}
        division={active}
        onExplore={(slug) => {
          setActive(null);
          navigate(`/${slug}`);
        }}
      />
    </section>
  );
};

export default Divisions;
